import React, { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Alert,
  Button,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  InputGroup,
  InputGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
} from '@patternfly/react-core';
import { EyeIcon, EyeSlashIcon } from '@patternfly/react-icons';
import { useApiFetch } from '../../../api';
import type { Mailbox } from '../../../types/models/Mailbox';
import NotificationRecipientsField from './NotificationRecipientsField';

interface MailboxPasswordModalProps {
  mailbox: Mailbox | null;
  onClose: () => void;
  domainName: string;
  mailDomainId: number;
}

function MailboxPasswordModal({ mailbox, onClose, domainName, mailDomainId }: MailboxPasswordModalProps) {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [notifyEnabled, setNotifyEnabled] = useState(true);
  const [notifyRecipients, setNotifyRecipients] = useState<string[]>(() => {
    const operatorEmail = auth.user?.profile?.email;
    return typeof operatorEmail === 'string' && operatorEmail.length > 0 ? [operatorEmail] : [];
  });
  const [notifyBufferHasInput, setNotifyBufferHasInput] = useState(false);

  const isPasswordValid = newPassword.length >= 8;
  const isConfirmValid = confirmPassword.length > 0 && confirmPassword === newPassword;
  const isNotifyValid = !notifyEnabled || (notifyRecipients.length > 0 && !notifyBufferHasInput);
  const isFormValid = isPasswordValid && isConfirmValid && isNotifyValid;

  const resetAndClose = () => {
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setShowPassword(false);
    setNotifyEnabled(true);
    const operatorEmail = auth.user?.profile?.email;
    setNotifyRecipients(typeof operatorEmail === 'string' && operatorEmail.length > 0 ? [operatorEmail] : []);
    setNotifyBufferHasInput(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!mailbox) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const body: Record<string, unknown> = { password: newPassword };
      if (notifyEnabled && notifyRecipients.length > 0) {
        body.notification_emails = notifyRecipients;
      }

      const params = new URLSearchParams({ mail_domain_id: String(mailDomainId) });
      const response = await apiFetch(`/emails/mailboxes/${mailbox.id}/password?${params.toString()}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error((body as { message?: string })?.message ?? `HTTP error! status: ${response.status}`);
      }

      resetAndClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal variant="small" isOpen={mailbox !== null} onClose={resetAndClose} aria-label="Change mailbox password">
      <ModalHeader title={`Change password — ${mailbox?.user}@${domainName}`} />
      <ModalBody>
        {error && (
          <Alert variant="danger" isInline title="Password change failed" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        <Form>
          <FormGroup label="New password" isRequired fieldId="change-password-new">
            <InputGroup>
              <InputGroupItem isFill>
                <TextInput
                  id="change-password-new"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(_event, value) => setNewPassword(value)}
                  validated={newPassword && !isPasswordValid ? 'error' : 'default'}
                />
              </InputGroupItem>
              <InputGroupItem>
                <Button
                  variant="control"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                </Button>
              </InputGroupItem>
            </InputGroup>
            {newPassword && !isPasswordValid && (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">Password must be at least 8 characters.</HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>
          <FormGroup label="Confirm new password" isRequired fieldId="change-password-confirm">
            <InputGroup>
              <InputGroupItem isFill>
                <TextInput
                  id="change-password-confirm"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(_event, value) => setConfirmPassword(value)}
                  validated={confirmPassword && !isConfirmValid ? 'error' : 'default'}
                />
              </InputGroupItem>
              <InputGroupItem>
                <Button
                  variant="control"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                </Button>
              </InputGroupItem>
            </InputGroup>
            {confirmPassword && !isConfirmValid && (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">Passwords do not match.</HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>
          <NotificationRecipientsField
            enabled={notifyEnabled}
            onEnabledChange={setNotifyEnabled}
            recipients={notifyRecipients}
            onRecipientsChange={setNotifyRecipients}
            hasUnchipifiedInput={notifyBufferHasInput}
            onHasUnchipifiedInputChange={setNotifyBufferHasInput}
          />
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          onClick={handleSubmit}
          isDisabled={!isFormValid || isSubmitting}
          isLoading={isSubmitting}
        >
          Change password
        </Button>
        <Button variant="link" onClick={resetAndClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default MailboxPasswordModal;
